import { Checkbox, FormControl, InputLabel, ListItemText, makeStyles, MenuItem, Select } from "@material-ui/core";
import React, { useCallback, useMemo } from "react";
import { EventTypeFilterFormObj } from "../../../hooks/useLogFilterRequest";
import { EventTypeOption } from "./ManagementEventOptions";

const useStyles = makeStyles(() => ({
    formControl: {
        minWidth: 160,
        flexGrow: 2,
        marginLeft: '10px',
        flexBasis: 160,
        "@media (max-width: 600px)": {
            width: "100%",
            minWidth: 0,
            flexBasis: "100%",
            marginLeft: 0,
        },
    },
}));

interface Props {
    value: EventTypeFilterFormObj[];
    onChange: (value: EventTypeFilterFormObj[]) => void;
    eventTypeOptions: EventTypeOption[];
    eventTypeLabel?: string;
    eventNameLabel?: string;
    isMultiSelect?: boolean;
}

export function EventTypeAndNameSelect({
    value,
    onChange,
    eventTypeOptions,
    eventTypeLabel = "Event Type",
    eventNameLabel = "Event",
    isMultiSelect = true
}: Props) {
    const classes = useStyles();

    const selectedEventTypes = useMemo(() => {
        return value.map(et => et.eventType);
    }, [value]);

    const selectedEventNames = useMemo(() => {
        return value.flatMap(et =>
            et.eventNames.map(name => `${et.eventType}-${name}`)
        );
    }, [value]);

    const renderEventTypeValue = useCallback((selected: unknown) => {
        return eventTypeOptions
            .filter(option => (selected as number[]).includes(option.value))
            .map(option => option.label)
            .join(', ');
    }, [eventTypeOptions]);

    const renderEventNameValue = useCallback((selected: unknown) => {
        return (selected as string[])
            .map(key => {
                const [eventTypeStr, eventNameStr] = key.split('-');
                const eventType = parseInt(eventTypeStr, 10);
                const eventName = parseInt(eventNameStr, 10);
                const eventTypeOption = eventTypeOptions.find(et => et.value === eventType);
                return eventTypeOption?.eventNames.find(en => en.value === eventName)?.label;
            })
            .filter((label): label is string => Boolean(label))
            .join(', ');
    }, [eventTypeOptions]);

    const handleEventTypeChange = useCallback((
        e: React.ChangeEvent<{ value: unknown }>
    ) => {
        if (isMultiSelect) {
            const selectedTypes = e.target.value as number[];
            if (selectedTypes.includes(-1)) {
                onChange([]);
                return;
            }

            const updatedFilters = value.filter(et =>
                selectedTypes.includes(et.eventType)
            );

            selectedTypes.forEach(typeValue => {
                if (!updatedFilters.find(et => et.eventType === typeValue)) {
                    updatedFilters.push({
                        eventType: typeValue,
                        eventNames: [],
                    });
                }
            });

            onChange(updatedFilters);
        }
        else {
            const selectedType = e.target.value as number;
            if (selectedType === -1) {
                onChange([]);
                return;
            }

            const updatedFilters = value.filter(et => selectedType === et.eventType);

            if (!updatedFilters.find(et => et.eventType === selectedType)) {
                updatedFilters.push({
                    eventType: selectedType,
                    eventNames: [],
                });
            }

            onChange(updatedFilters);
        }
    }, [isMultiSelect, value, onChange]);

    const handleEventNameChange = useCallback((
        e: React.ChangeEvent<{ value: unknown }>
    ) => {
        if (isMultiSelect) {
            const selectedKeys = e.target.value as string[];

            if (selectedKeys.includes("-1")) {
                const updatedFilters = selectedEventTypes.map(eventType => ({
                    eventType,
                    eventNames: [],
                }));

                onChange(updatedFilters);
                return;
            }

            const eventNameMap: { [key: number]: number[] } = {};

            selectedKeys.forEach(key => {
                const [eventTypeStr, eventNameStr] = key.split('-');
                const eventType = parseInt(eventTypeStr, 10);
                const eventName = parseInt(eventNameStr, 10);

                if (!eventNameMap[eventType]) {
                    eventNameMap[eventType] = [];
                }
                eventNameMap[eventType].push(eventName);
            });

            const updatedFilters = selectedEventTypes.map(eventType => ({
                eventType,
                eventNames: eventNameMap[eventType] || [],
            }));

            onChange(updatedFilters);
        }
        else {
            const selectedKey = e.target.value as string;

            if (selectedKey === "-1") {
                const updatedFilters = selectedEventTypes.map(eventType => ({
                    eventType,
                    eventNames: [],
                }));

                onChange(updatedFilters);
                return;
            }

            const eventNameStr = selectedKey.split('-')[1];
            const eventName = parseInt(eventNameStr, 10);
            const updatedFilters = selectedEventTypes.map(eventType => ({
                eventType,
                eventNames: [eventName],
            }));
            onChange(updatedFilters);
        }
    }, [isMultiSelect, selectedEventTypes, onChange]);

    return (
        <>
            <FormControl className={classes.formControl}>
                <InputLabel htmlFor="event-type-select">{eventTypeLabel}</InputLabel>
                <Select
                    inputProps={{ id: "event-type-select" }}
                    SelectDisplayProps={{ "data-testid": "event-type-select-input" } as React.HTMLAttributes<HTMLDivElement>}
                    value={selectedEventTypes}
                    onChange={handleEventTypeChange}
                    renderValue={renderEventTypeValue}
                    multiple={isMultiSelect}
                >
                    <MenuItem key="-1" value={-1} data-testid="event-type-clear-option"></MenuItem>
                    {eventTypeOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {isMultiSelect && (
                                <Checkbox color="primary" checked={selectedEventTypes.includes(option.value)} />
                            )}
                            <ListItemText primary={option.label} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl className={classes.formControl} disabled={selectedEventTypes.length === 0}>
                <InputLabel htmlFor="event-name-select">{eventNameLabel}</InputLabel>
                <Select
                    inputProps={{ id: "event-name-select" }}
                    SelectDisplayProps={{ "data-testid": "event-name-select-input" } as React.HTMLAttributes<HTMLDivElement>}
                    value={selectedEventNames}
                    onChange={handleEventNameChange}
                    renderValue={renderEventNameValue}
                    multiple={isMultiSelect}
                >

                    <MenuItem key="deselect" value="-1" data-testid="event-name-clear-option"></MenuItem>
                    {selectedEventTypes.map(eventType => {
                        const eventTypeOption = eventTypeOptions.find(et => et.value === eventType);
                        if (!eventTypeOption) return null;

                        return [
                            (isMultiSelect && <MenuItem key={`header-${eventType}`} disabled style={{ fontWeight: 'bold', opacity: 0.6 }}>
                                {eventTypeOption.label}
                            </MenuItem>),
                            ...eventTypeOption.eventNames.map(eventName => {
                                const key = `${eventType}-${eventName.value}`;
                                return (
                                    <MenuItem
                                        key={key}
                                        value={key}
                                        style={{ paddingLeft: '24px' }}
                                    >
                                        {isMultiSelect && (
                                            <Checkbox color="primary" checked={selectedEventNames.includes(key)} />
                                        )}
                                        <ListItemText primary={eventName.label} />
                                    </MenuItem>
                                );
                            })
                        ];
                    })}
                </Select>
            </FormControl>
        </>
    );
}